// JIRA - https://performyard.atlassian.net/browse/PY-8473
// dependencies
import { cva } from "class-variance-authority";
import * as React from "react";

//utils
import { cn } from "../../../lib/utils";
import { ALLOWED_TEXT_OVERRIDE_ELEMENTS, fontSizeToElementMap } from "./utils";

//theme
import { ColorShade, FontWeight, TextSize, fontWeightsMap, textColorShadeMapping, textSizesMap } from "../../../theme";

const textVariants = cva("whitespace-normal", {
	variants: {
		color: textColorShadeMapping,
		size: textSizesMap,
		weight: fontWeightsMap,
		italic: {
			true: "italic",
		},
		underline: {
			true: "underline",
		},
		lineThrough: {
			true: "line-through",
		},
		wrap: {
			false: "whitespace-nowrap",
		},
		wordBreak: {
			true: "break-words",
		},
	},
	defaultVariants: {
		color: undefined,
		size: "paragraph-3",
		weight: "regular",
		italic: false,
		underline: false,
		lineThrough: false,
		wrap: true,
		wordBreak: true,
	},
});

export type TextProps = {
	/**
	 * The color of the text
	 */
	color?: ColorShade;
	/**
	 * The size of the text typography
	 */
	size: TextSize;
	/**
	 *  Font weight
	 */
	weight?: FontWeight;
	/**
	 *  Child Node
	 */
	children: React.ReactNode;

	/**
	 * Overrides the default HTML element that would be rendered.
	 * By default, the component automatically selects an appropriate HTML element (h1-h6, p, span)
	 * based on the size prop using the fontSizeToElementMap.
	 * @default undefined
	 */
	overrideElement?: ALLOWED_TEXT_OVERRIDE_ELEMENTS;

	/**
	 * is italic text
	 * @default false
	 */
	italic?: boolean;

	/**
	 * is underline text
	 * @default false
	 */
	underline?: boolean;

	/**
	 * is line-through text
	 * @default false
	 */
	lineThrough?: boolean;

	/**
	 * does text wrap when it reaches the end of its container
	 * @default true
	 */
	wrap?: boolean;

	/**
	 * do we break words that are larger than the given width?
	 * @default true
	 */
	wordBreak?: boolean;

	/**
	 * htmlFor attribute to add to the text element. Useful for binding a label to an input element.
	 */
	htmlFor?: string;

	/**
	 * testID attribute to add to the text element. Useful for testing.
	 */
	"data-testid"?: string;

	/**
	 * id of the element
	 */
	id?: string;

	/**
	 *  Class Name
	 */
	dangerouslySetClassName?: string;
};

/**
 * Text component that wraps children in expected Typography styles
 */
export const Text = React.forwardRef<
	HTMLHeadElement | HTMLParagraphElement | HTMLSpanElement | HTMLLabelElement | HTMLLegendElement,
	TextProps
>(function Text(
	props: TextProps,
	ref: React.Ref<HTMLHeadElement | HTMLParagraphElement | HTMLSpanElement | HTMLLabelElement | HTMLLegendElement>
) {
	const {
		color,
		size,
		weight,
		children,
		dangerouslySetClassName,
		italic = false,
		underline = false,
		lineThrough = false,
		wrap = true,
		wordBreak = true,
		htmlFor = undefined,
		id,
	} = props;

	const element = props.overrideElement || fontSizeToElementMap[size];

	return React.createElement(
		element,
		{
			ref,
			className: cn(
				textVariants({ color, size, weight, italic, underline, lineThrough, wrap, wordBreak }),
				dangerouslySetClassName
			),
			htmlFor: htmlFor,
			"data-testid": props?.["data-testid"],
			id,
		},
		children
	);
});

Text.displayName = "Text";
